/**
 * Committee Utility Functions
 * 
 * These utilities handle committee name formatting and provide clear
 * indication that House and Senate committees are separate entities.
 */

export interface Committee {
  name: string;
  chamber: string;
}

/**
 * Format committee name with chamber prefix for clear display
 * This makes it obvious that committees are chamber-specific
 * 
 * @param name - Committee name (e.g., "Appropriations")
 * @param chamber - Chamber (e.g., "House", "Senate", "Joint")
 * @returns Formatted name (e.g., "House Appropriations")
 */
export const formatCommitteeName = (name: string, chamber: string): string => {
  if (!name || !chamber) {
    return name || 'Unknown Committee';
  }

  const normalizedChamber = chamber.trim();
  
  // Joint committees typically already have "Joint" in their name
  if (normalizedChamber === 'Joint' || normalizedChamber === 'Joint Committee') {
    // Don't double-prefix if name already contains "Joint"
    if (name.toLowerCase().includes('joint')) {
      return name;
    }
    return `Joint ${name}`;
  }
  
  // For House and Senate, always prefix to make separation clear
  return `${normalizedChamber} ${name}`;
};

/**
 * Get a short display name (original behavior for backwards compatibility)
 * Use this when space is limited and chamber is shown separately
 */
export const getCommitteeNameShort = (name: string): string => {
  return name;
};

/**
 * Get committee chamber display with color coding
 * Helps visually distinguish chambers
 */
export const getCommitteeChampberDisplay = (chamber: string): {
  label: string;
  className: string;
} => {
  const normalizedChamber = chamber?.trim() || 'Unknown';
  
  switch (normalizedChamber) {
    case 'House':
      return {
        label: 'House',
        className: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
      };
    case 'Senate':
      return {
        label: 'Senate', 
        className: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
      };
    case 'Joint':
    case 'Joint Committee':
      return {
        label: 'Joint',
        className: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
      };
    default:
      return {
        label: normalizedChamber,
        className: 'bg-background-tertiary text-neutral-800 dark:bg-background-dark-primary dark:text-text-dark-primary'
      };
  }
};

/**
 * Sort committees with logical chamber ordering
 * House -> Senate -> Joint -> Others
 */
export const sortCommittees = (committees: Committee[]): Committee[] => {
  return committees.sort((a, b) => {
    // First sort by chamber priority
    const getChamberPriority = (chamber: string): number => {
      switch (chamber?.trim()) {
        case 'House': return 1;
        case 'Senate': return 2;
        case 'Joint': 
        case 'Joint Committee': return 3;
        default: return 4;
      }
    };
    
    const chamberDiff = getChamberPriority(a.chamber) - getChamberPriority(b.chamber);
    if (chamberDiff !== 0) {
      return chamberDiff;
    }
    
    // Then sort by name within chamber
    return a.name.localeCompare(b.name);
  });
};

/**
 * Group committees by name to show chamber relationships
 * This helps identify when committees exist across multiple chambers
 */
export const groupCommitteesByName = (committees: Committee[]): Record<string, Committee[]> => {
  return committees.reduce((groups, committee) => {
    const name = committee.name;
    if (!groups[name]) {
      groups[name] = [];
    }
    groups[name].push(committee);
    return groups;
  }, {} as Record<string, Committee[]>);
};

/**
 * Check if a committee name exists in multiple chambers
 * Useful for highlighting potential confusion
 */
export const isMultiChamberCommittee = (committeeName: string, allCommittees: Committee[]): boolean => {
  const chambers = new Set(
    allCommittees
      .filter(c => c.name === committeeName)
      .map(c => c.chamber)
  );
  return chambers.size > 1;
};

/**
 * Get all chambers for a committee name
 */
export const getCommitteeChambers = (committeeName: string, allCommittees: Committee[]): string[] => {
  return Array.from(new Set(
    allCommittees
      .filter(c => c.name === committeeName)
      .map(c => c.chamber)
  )).sort();
};

/**
 * Validation function for development/debugging
 * Helps identify potential committee normalization issues
 */
export const validateCommitteeStructure = (committees: Committee[]): {
  valid: boolean;
  warnings: string[];
  multiChamberCommittees: Record<string, string[]>;
} => {
  const warnings: string[] = [];
  const grouped = groupCommitteesByName(committees);
  const multiChamberCommittees: Record<string, string[]> = {};
  
  Object.entries(grouped).forEach(([name, committees]) => {
    if (committees.length > 1) {
      const chambers = committees.map(c => c.chamber);
      multiChamberCommittees[name] = chambers;
      
      // Check for potential issues
      if (chambers.includes('Joint') && chambers.length > 1) {
        warnings.push(`Committee "${name}" exists as both Joint and chamber-specific`);
      }
      
      if (!chambers.includes('Joint') && chambers.length > 1) {
        warnings.push(`Committee "${name}" exists in multiple chambers: ${chambers.join(', ')}`);
      }
    }
  });
  
  return {
    valid: warnings.length === 0,
    warnings,
    multiChamberCommittees
  };
};

/**
 * Format committee for API responses to maintain consistency
 */
export const formatCommitteeForAPI = (committee: Committee): Committee & { formatted_name: string } => {
  return {
    ...committee,
    formatted_name: formatCommitteeName(committee.name, committee.chamber)
  };
};
